import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DevTokensData } from '@sb/feathers-types';
import { mapPaginated } from '@sb/feathers-types';

import { getService } from '../utils';

export const useDevTokens = () => {
  const [tokens, setTokens] = useState<DevTokensData[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Mirror tokens into a ref so deleteTokensBySource can read the latest list
  // without taking tokens as a useCallback dep — keeping the callback identity
  // stable across token updates so consumers using it as a useEffect dep don't
  // re-fire on every create/remove.
  const tokensRef = useRef<DevTokensData[]>(tokens);

  useEffect(() => {
    tokensRef.current = tokens;
  }, [tokens]);

  useEffect(() => {
    const subscription = getService('devTokens')()
      .watch()
      .find({})
      .subscribe((response) => {
        setLoaded(true);
        const newTokens = mapPaginated(response, (d) => d);
        setTokens(newTokens);
      });

    return () => subscription.unsubscribe();
  }, []);

  /** Create a persistent user token. Returns plaintext (show-once). */
  const createToken = useCallback(
    async (name: string, expiresAt?: string): Promise<string | null> => {
      const service = getService('devTokens');

      const result = await service().create({
        name,
        source: 'user',
        ephemeral: false,
        ...(expiresAt ? { expires_at: expiresAt } : {}),
      });

      return (result as DevTokensData).token ?? null;
    },
    [],
  );

  /** Create an ephemeral token for programmatic use (teleop, etc). */
  const createEphemeralToken = useCallback(
    async (
      name: string,
      source: string,
      expiresAt?: string,
    ): Promise<string | null> => {
      const service = getService('devTokens');

      const result = await service().create({
        name,
        source,
        ephemeral: true,
        ...(expiresAt ? { expires_at: expiresAt } : {}),
      });

      return (result as DevTokensData).token ?? null;
    },
    [],
  );

  const deleteToken = useCallback(async (id: string) => {
    const service = getService('devTokens');
    await service().remove(id);
  }, []);

  /** Delete all tokens with a given source (e.g., "ios_teleop"). */
  const deleteTokensBySource = useCallback(async (source: string) => {
    const toDelete = tokensRef.current.filter((t) => t.source === source);
    const service = getService('devTokens');

    for (const t of toDelete) {
      await service().remove(t.id);
    }
  }, []);

  // Split tokens for UI display
  const userTokens = useMemo(
    () => tokens.filter((t) => !t.ephemeral),
    [tokens],
  );

  const sessionTokens = useMemo(
    () => tokens.filter((t) => t.ephemeral),
    [tokens],
  );

  return {
    tokens,
    userTokens,
    sessionTokens,
    createToken,
    createEphemeralToken,
    deleteToken,
    deleteTokensBySource,
    loaded,
  } as const;
};
